import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Palette,
  Upload,
  Eye,
  Save,
  RefreshCw,
  Type,
  Globe,
  Mail,
  Lock,
  Image,
  Monitor,
  Sun,
  Moon,
  Sparkles,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COLOR_PRESETS = [
  { name: "Indigo", primary: "#6366f1", accent: "#f59e0b" },
  { name: "Emerald", primary: "#10b981", accent: "#6366f1" },
  { name: "Rose", primary: "#f43f5e", accent: "#14b8a6" },
  { name: "Amber", primary: "#f59e0b", accent: "#6366f1" },
  { name: "Violet", primary: "#8b5cf6", accent: "#ec4899" },
  { name: "Cyan", primary: "#06b6d4", accent: "#f43f5e" },
  { name: "Blue", primary: "#3b82f6", accent: "#f59e0b" },
  { name: "Orange", primary: "#f97316", accent: "#3b82f6" },
];

const FONT_OPTIONS = [
  { value: "system-ui", label: "System Default" },
  { value: "Inter", label: "Inter" },
  { value: "Poppins", label: "Poppins" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Lato", label: "Lato" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Playfair Display", label: "Playfair Display" },
];

export default function BrandingSettings() {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const res = await api.get("/branding");
      setBranding(res.data);
      setHasAccess(true);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.code === "FEATURE_GATED") {
        setHasAccess(false);
      } else {
        toast.error("Failed to load branding settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.put("/branding", branding);
      toast.success("Branding settings saved!");
    } catch (e) {
      if (e.response?.status === 403) {
        setHasAccess(false);
        toast.error("Custom branding requires Growth tier");
      } else {
        toast.error("Failed to save branding");
      }
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const applyColorPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      primary_color: preset.primary,
      accent_color: preset.accent
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="branding-loading">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="branding-gated">
        <div className="flex items-center gap-3">
          <Palette className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Custom Branding</h1>
            <p className="text-indigo-400">Customize your client-facing pages</p>
          </div>
        </div>
        
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-indigo-500 mb-2">Growth Tier Feature</h2>
            <p className="text-indigo-400 mb-6 max-w-md mx-auto">
              Custom branding is available on the Growth tier. Upgrade to customize your 
              client-facing pages with your own logo, colors, and domain.
            </p>
            <Button 
              onClick={() => window.location.href = "/billing"}
              className="bg-gradient-to-r from-indigo-500 to-indigo-500 hover:from-indigo-600 hover:to-indigo-600"
            >
              Upgrade to Growth
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Palette className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Custom Branding</h1>
            <p className="text-indigo-400">Customize your client-facing pages</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-slate-600 text-slate-400"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? "Hide" : "Show"} Preview
          </Button>
          <Button 
            onClick={saveBranding}
            disabled={saving}
            className="bg-indigo-500 hover:bg-slate-600"
            data-testid="save-branding-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding Form */}
        <div className="space-y-6">
          {/* Company Info */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <Type className="w-5 h-5 text-indigo-400" />
                Company Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Company Name</Label>
                <Input
                  placeholder="Your Company Name"
                  value={branding?.company_name || ""}
                  onChange={(e) => updateField("company_name", e.target.value)}
                  className="bg-slate-900 border-slate-700 text-indigo-500"
                  data-testid="company-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-400">Logo URL</Label>
                <Input
                  placeholder="https://your-site.com/logo.png"
                  value={branding?.logo_url || ""}
                  onChange={(e) => updateField("logo_url", e.target.value)}
                  className="bg-slate-900 border-slate-700 text-indigo-500"
                  data-testid="logo-url-input"
                />
                <p className="text-xs text-slate-400">
                  Recommended: 200x200px PNG with transparent background
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-400" />
                Colors
              </CardTitle>
              <CardDescription className="text-indigo-400">
                Choose colors that match your brand
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Color Presets */}
              <div>
                <Label className="text-slate-400 mb-2 block">Quick Presets</Label>
                <div className="grid grid-cols-4 gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyColorPreset(preset)}
                      className="group relative p-2 rounded-lg border border-slate-700 hover:border-slate-400 transition-colors"
                      title={preset.name}
                    >
                      <div className="flex gap-1">
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.primary }}
                        />
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.accent }}
                        />
                      </div>
                      {branding?.primary_color === preset.primary && (
                        <Check className="absolute -top-1 -right-1 w-4 h-4 text-green-400 bg-slate-800 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={branding?.primary_color || "#6366f1"}
                      onChange={(e) => updateField("primary_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-slate-900 border-slate-700 cursor-pointer"
                    />
                    <Input
                      value={branding?.primary_color || "#6366f1"}
                      onChange={(e) => updateField("primary_color", e.target.value)}
                      className="bg-slate-900 border-slate-700 text-indigo-500 font-mono"
                      data-testid="primary-color-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-400">Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={branding?.accent_color || "#f59e0b"}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-slate-900 border-slate-700 cursor-pointer"
                    />
                    <Input
                      value={branding?.accent_color || "#f59e0b"}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                      className="bg-slate-900 border-slate-700 text-indigo-500 font-mono"
                      data-testid="accent-color-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Style Settings */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-indigo-400" />
                Style Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Background Style</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "dark", icon: Moon, label: "Dark" },
                    { value: "light", icon: Sun, label: "Light" },
                    { value: "gradient", icon: Sparkles, label: "Gradient" }
                  ].map((style) => (
                    <button
                      key={style.value}
                      onClick={() => updateField("background_style", style.value)}
                      className={`p-3 rounded-lg border transition-colors flex flex-col items-center gap-2 ${
                        branding?.background_style === style.value
                          ? "border-indigo-500 bg-indigo-500/20"
                          : "border-slate-700 hover:border-slate-400"
                      }`}
                    >
                      <style.icon className={`w-5 h-5 ${
                        branding?.background_style === style.value ? "text-indigo-400" : "text-indigo-400"
                      }`} />
                      <span className={`text-sm ${
                        branding?.background_style === style.value ? "text-slate-400" : "text-indigo-400"
                      }`}>{style.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-400">Font Family</Label>
                <Select 
                  value={branding?.font_family || "system-ui"} 
                  onValueChange={(v) => updateField("font_family", v)}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {FONT_OPTIONS.map((font) => (
                      <SelectItem 
                        key={font.value} 
                        value={font.value} 
                        className="text-indigo-500"
                        style={{ fontFamily: font.value }}
                      >
                        {font.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label className="text-slate-400">Show "Powered by ClientKit"</Label>
                  <p className="text-xs text-slate-400">Display branding footer on client pages</p>
                </div>
                <Switch
                  checked={branding?.show_powered_by !== false}
                  onCheckedChange={(checked) => updateField("show_powered_by", checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Footer */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-400" />
                Email Footer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Custom footer text for emails sent to clients..."
                value={branding?.email_footer || ""}
                onChange={(e) => updateField("email_footer", e.target.value)}
                className="bg-slate-900 border-slate-700 text-indigo-500 min-h-[100px]"
                data-testid="email-footer-input"
              />
              <p className="text-xs text-slate-400 mt-2">
                This will appear at the bottom of all client-facing emails
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        {previewMode && (
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700/50 sticky top-4">
              <CardHeader>
                <CardTitle className="text-indigo-500 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-green-400" />
                  Live Preview
                </CardTitle>
                <CardDescription className="text-indigo-400">
                  See how your branding looks on client pages
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Mini Preview */}
                <div 
                  className="rounded-lg overflow-hidden border border-slate-700"
                  style={{ 
                    backgroundColor: branding?.background_style === 'light' ? '#f8fafc' : '#0f172a',
                    fontFamily: branding?.font_family || 'system-ui'
                  }}
                >
                  {/* Header */}
                  <div 
                    className="p-4 border-b"
                    style={{ borderColor: branding?.background_style === 'light' ? '#e2e8f0' : '#334155' }}
                  >
                    <div className="flex items-center gap-3">
                      {branding?.logo_url ? (
                        <img src={branding.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: branding?.primary_color || '#6366f1' }}
                        >
                          <span className="text-indigo-500 font-bold">
                            {(branding?.company_name || "C")[0]}
                          </span>
                        </div>
                      )}
                      <span 
                        className="font-semibold"
                        style={{ color: branding?.background_style === 'light' ? '#0f172a' : '#f8fafc' }}
                      >
                        {branding?.company_name || "Your Company"}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <h2 
                      className="text-xl font-bold"
                      style={{ color: branding?.background_style === 'light' ? '#0f172a' : '#f8fafc' }}
                    >
                      Project Proposal
                    </h2>
                    <p 
                      className="text-sm"
                      style={{ color: branding?.background_style === 'light' ? '#64748b' : '#94a3b8' }}
                    >
                      Thank you for considering us for your project. Here are the details...
                    </p>
                    
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 rounded-lg text-indigo-500 text-sm font-medium"
                        style={{ backgroundColor: branding?.primary_color || '#6366f1' }}
                      >
                        Accept Proposal
                      </button>
                      <button
                        className="px-4 py-2 rounded-lg text-sm font-medium"
                        style={{ 
                          backgroundColor: `${branding?.accent_color || '#f59e0b'}20`,
                          color: branding?.accent_color || '#f59e0b'
                        }}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  {branding?.show_powered_by !== false && (
                    <div 
                      className="p-3 text-center text-xs border-t"
                      style={{ 
                        borderColor: branding?.background_style === 'light' ? '#e2e8f0' : '#334155',
                        color: branding?.background_style === 'light' ? '#94a3b8' : '#64748b'
                      }}
                    >
                      Powered by ClientKit
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-slate-400 mt-4 text-center">
                  This is a simplified preview. Actual pages may vary slightly.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
